import React, { useState } from 'react';
import styles from './PermissionSelector.module.css';

/**
 * Component for selecting permissions grouped by category
 * 
 * @param {Object} permissions - Permissions grouped by category (from API)
 * @param {Array} selectedPermissions - Array of selected permission names
 * @param {Function} onChange - Callback when selection changes
 */
export default function PermissionSelector({ permissions, selectedPermissions, onChange }) {
  const [collapsedCategories, setCollapsedCategories] = useState(new Set());
  const toggleCategoryCollapse = (categoryName) => {
    setCollapsedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryName)) {
        newSet.delete(categoryName);
      } else {
        newSet.add(categoryName);
      }
      return newSet;
    });
  };

  const handleToggle = (permissionName) => {
    const isSelected = selectedPermissions.includes(permissionName);
    
    if (isSelected) {
      onChange(selectedPermissions.filter(p => p !== permissionName));
    } else {
      onChange([...selectedPermissions, permissionName]);
    }
  };

  const handleToggleCategory = (category) => {
    const categoryPermissions = permissions[category] || [];
    const categoryPermissionNames = categoryPermissions.map(p => p.name);
    
    const allSelected = categoryPermissionNames.every(name => 
      selectedPermissions.includes(name)
    );

    if (allSelected) {
      // Deselect all in category
      onChange(selectedPermissions.filter(p => !categoryPermissionNames.includes(p)));
    } else {
      // Select all in category
      const newPermissions = [...selectedPermissions];
      categoryPermissionNames.forEach(name => {
        if (!newPermissions.includes(name)) {
          newPermissions.push(name);
        }
      });
      onChange(newPermissions);
    }
  };

  // Category display names
  const categoryNames = {
    dashboard: 'Dashboard',
    leads: 'Leads',
    projects: 'Projects',
    people: 'People',
    directory: 'Directory',
    schedule: 'Schedule',
    timesheets: 'Timesheets',
    profile: 'Profile',
    settings: 'Settings',
    time_off: 'Time Off'
  };

  return (
    <div className={styles.permissionSelector}>
      {Object.keys(permissions).map(category => {
        const categoryPermissions = permissions[category] || [];
        const categoryPermissionNames = categoryPermissions.map(p => p.name);
        const allSelected = categoryPermissionNames.length > 0 && 
          categoryPermissionNames.every(name => selectedPermissions.includes(name));
        const someSelected = categoryPermissionNames.some(name => 
          selectedPermissions.includes(name)
        );
        const isCollapsed = collapsedCategories.has(category);

        return (
          <div key={category} className={styles.category}>
            <div className={styles.categoryHeader}>
              <button
                type="button"
                className={styles.collapseButton}
                onClick={() => toggleCategoryCollapse(category)}
                aria-label={isCollapsed ? 'Expand category' : 'Collapse category'}
              >
                {isCollapsed ? '▶' : '▼'}
              </button>
              <label className={styles.categoryLabel}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={input => {
                    if (input) input.indeterminate = someSelected && !allSelected;
                  }}
                  onChange={() => handleToggleCategory(category)}
                />
                <strong>{categoryNames[category] || category}</strong>
              </label>
            </div>
            
            {!isCollapsed && (
              <div className={styles.permissions}>
                {categoryPermissions.map(perm => (
                  <label key={perm.permission_id} className={styles.permissionLabel}>
                    <input
                      type="checkbox"
                      checked={selectedPermissions.includes(perm.name)}
                      onChange={() => handleToggle(perm.name)}
                    />
                    <span>{perm.description || perm.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}